import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatBubbleStamp,
  formatDayLabel,
  resolveBubbleDateLanguage,
  spokenBubbleStamp,
  tBubbleDateCopy,
} from './bubbleDate.functions';

const NAMESPACE = 'chat/chat';

// Used only when a key does not resolve (namespace still loading, or the
// key-echo `t` of the test setup), so a stamp never shows raw key paths.
const FALLBACK_COPY: tBubbleDateCopy = {
  months: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
  today: 'Hoy',
  yesterday: 'Ayer',
  am: 'a.m.',
  pm: 'p.m.',
  clock: '12',
  stamp: '{{day}} {{month}} {{time}}',
  stampWithYear: '{{day}} {{month}} {{year}}, {{time}}',
  day: '{{day}} {{month}}',
  dayWithYear: '{{day}} {{month}} {{year}}',
};

type tTranslate = (key: string, options?: Record<string, unknown>) => unknown;

function readList(t: tTranslate, field: 'months' | 'weekdays') {
  const value = t(`bubbleDate.${field}`, { returnObjects: true });
  const expected = field === 'months' ? 12 : 7;
  return Array.isArray(value) && value.length === expected
    ? value.map(String)
    : FALLBACK_COPY[field];
}

function readText(
  t: tTranslate,
  field: Exclude<keyof tBubbleDateCopy, 'months' | 'weekdays'>,
) {
  const key = `bubbleDate.${field}`;
  const value = t(key);
  return typeof value === 'string' && value !== key ? value : FALLBACK_COPY[field];
}

/**
 * The conversation's date formatting with the active copy: `stamp` for the
 * date and time next to a message, `spoken` for screen readers, and
 * `dayLabel` for the day separators.
 */
export default function useBubbleDate() {
  const { t, i18n, ready } = useTranslation(NAMESPACE);
  const language = resolveBubbleDateLanguage(
    i18n?.language,
    import.meta.env.VITE_ENV,
  );

  const copy = useMemo<tBubbleDateCopy>(() => {
    // Production pins dates to Spanish even when the interface switches
    // language, like moment does in src/i18n.ts.
    const fixed =
      (i18n as { getFixedT?: (lng: string, ns: string) => tTranslate })
        ?.getFixedT?.(language, NAMESPACE) ?? (t as tTranslate);
    return {
      months: readList(fixed, 'months'),
      weekdays: readList(fixed, 'weekdays'),
      today: readText(fixed, 'today'),
      yesterday: readText(fixed, 'yesterday'),
      am: readText(fixed, 'am'),
      pm: readText(fixed, 'pm'),
      clock: readText(fixed, 'clock'),
      stamp: readText(fixed, 'stamp'),
      stampWithYear: readText(fixed, 'stampWithYear'),
      day: readText(fixed, 'day'),
      dayWithYear: readText(fixed, 'dayWithYear'),
    };
    // `ready` flips once the namespace arrives, so the fallback gives way to it.
  }, [t, i18n, language, ready]);

  return useMemo(
    () => ({
      language,
      stamp: (date: string) => formatBubbleStamp(date, { copy }),
      spoken: (date: string) => spokenBubbleStamp(date, language),
      dayLabel: (date: string) => formatDayLabel(date, { copy }),
    }),
    [copy, language],
  );
}
